import { useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { AddTaskDialog } from "./AddTaskDialog";
import { type EditableTask } from "./use-tasks";
import { FreightDetailDialog, type FreightDetail } from "./FreightDetailDialog";

type CalendarEventType = "task" | "etd" | "eta";

type CalendarEvent = {
  date: string; // "YYYY-MM-DD"
  label: string;
  type: CalendarEventType;
  vendor?: string;
  taskId?: number | string;
  freightId?: number;
};

type TaggedRef = {
  id: number | string;
  name: string;
  slug: string;
  chip_color: string | null;
};

type RawOpsTask = {
  id: number | string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  category: string | null;
  due_date: string | null;
  vendor: string | null;
  people: TaggedRef[];
  vendors: TaggedRef[];
};

type RawFreight = {
  id: number;
  booking: string | null;
  carrier: string | null;
  etd: string | null;
  eta: string | null;
  vendor: string | null;
  origin: string | null;
  destination: string | null;
  container: string | null;
  status: string | null;
};

const TYPE_STYLES: Record<CalendarEventType, { dot: string; label: string; chip: string }> = {
  task: { dot: "bg-orange-400", label: "Task", chip: "bg-orange-50 text-orange-700 border-orange-200" },
  etd: { dot: "bg-blue-500", label: "ETD", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  eta: { dot: "bg-emerald-500", label: "ETA", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function dateKey(year: number, month: number, day: number) {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

export function CalendarPage() {
  const api = useApi();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [editingTask, setEditingTask] = useState<EditableTask | null>(null);
  const [viewingFreight, setViewingFreight] = useState<FreightDetail | null>(null);
  const [viewingFreightAlert, setViewingFreightAlert] = useState<"etd" | "eta" | null>(null);

  const tasksQ = useQuery({
    queryKey: ["operations_tasks"],
    queryFn: async (): Promise<RawOpsTask[]> => {
      const res = await api(`/api/operations_tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawOpsTask[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const freightQ = useQuery({
    queryKey: ["freight"],
    queryFn: async (): Promise<RawFreight[]> => {
      const res = await api(`/api/freight`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawFreight[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const events: CalendarEvent[] = useMemo(() => {
    const out: CalendarEvent[] = [];
    for (const t of tasksQ.data ?? []) {
      if (t.due_date && t.status !== "done") {
        out.push({
          date: t.due_date.slice(0, 10),
          label: t.title,
          type: "task",
          vendor: t.vendor ?? undefined,
          taskId: t.id,
        });
      }
    }
    for (const f of freightQ.data ?? []) {
      if (f.etd) {
        out.push({
          date: f.etd.slice(0, 10),
          label: `${f.booking ?? ""} ETD — ${f.origin ?? ""}`.trim(),
          type: "etd",
          vendor: f.vendor ?? undefined,
          freightId: f.id,
        });
      }
      if (f.eta) {
        out.push({
          date: f.eta.slice(0, 10),
          label: `${f.booking ?? ""} ETA — ${f.destination ?? ""}`.trim(),
          type: "eta",
          vendor: f.vendor ?? undefined,
          freightId: f.id,
        });
      }
    }
    return out;
  }, [tasksQ.data, freightQ.data]);

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleString("default", { month: "long" });

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  }

  function goToday() {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  }

  function openTask(taskId: number | string) {
    const task = (tasksQ.data ?? []).find((t) => t.id === taskId);
    if (!task) return;
    const editable: EditableTask = {
      id: task.id,
      title: task.title,
      description: task.description || null,
      status: task.status,
      priority: task.priority,
      category: task.category || "Operations",
      due_date: task.due_date,
      people: (task.people ?? []).map((p) => ({ id: p.id, name: p.name })),
      vendors: (task.vendors ?? []).map((v) => ({ id: v.id, name: v.name })),
    };
    setEditingTask(editable);
  }

  function openFreight(freightId: number, alertType: "etd" | "eta") {
    const freight = (freightQ.data ?? []).find((f) => f.id === freightId);
    if (!freight) return;
    setViewingFreight(freight);
    setViewingFreightAlert(alertType);
  }

  function handleEventClick(ev: CalendarEvent, e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (ev.type === "task" && ev.taskId !== undefined) {
      openTask(ev.taskId);
    } else if ((ev.type === "etd" || ev.type === "eta") && ev.freightId !== undefined) {
      openFreight(ev.freightId, ev.type);
    }
  }

  // Build event map for current month
  const eventMap: Record<string, CalendarEvent[]> = {};
  events.forEach((ev) => {
    if (!eventMap[ev.date]) eventMap[ev.date] = [];
    eventMap[ev.date].push(ev);
  });

  // Upcoming events (next 10, sorted by date, from today forward)
  const todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  const upcoming = [...events]
    .filter((ev) => ev.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 10);

  // Build grid cells
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div
      className="flex flex-col h-full"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      {/* Header */}
      <div className="px-8 py-5 border-b bg-background flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Calendar</h1>
            <p className="text-xs text-muted-foreground">Task deadlines, ETDs, and ETAs</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4">
          {(["task", "etd", "eta"] as CalendarEventType[]).map((type) => (
            <div key={type} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${TYPE_STYLES[type].dot}`} />
              <span className="text-xs text-muted-foreground">{TYPE_STYLES[type].label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Calendar Main */}
        <div className="flex-1 overflow-auto p-4 bg-[#f8fafc]">
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Month Nav */}
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
              </button>
              <div className="flex items-center gap-3">
                <span className="text-base font-semibold text-foreground">
                  {monthName} {viewYear}
                </span>
                <button
                  onClick={goToday}
                  className="text-xs font-medium px-2.5 py-1 rounded-lg border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  Today
                </button>
              </div>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 border-b">
              {DAYS_OF_WEEK.map((d) => (
                <div key={d} className="py-2 text-center text-[11px] font-semibold text-muted-foreground">
                  {d}
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-7 divide-x divide-y divide-border">
              {cells.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} className="min-h-[140px] bg-muted/10" />;
                }
                const key = dateKey(viewYear, viewMonth, day);
                const dayEvents = eventMap[key] ?? [];
                const isToday =
                  day === today.getDate() &&
                  viewMonth === today.getMonth() &&
                  viewYear === today.getFullYear();

                return (
                  <div key={key} className={`min-h-[140px] p-2 hover:bg-muted/20 transition-colors ${isToday ? "bg-blue-50/60" : ""}`}>
                    <div className={`text-xs font-medium mb-1.5 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-blue-600 text-white" : "text-foreground"}`}>
                      {day}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 4).map((ev, i) => (
                        <div
                          key={i}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleEventClick(ev, e)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleEventClick(ev, e);
                            }
                          }}
                          className={`flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded border truncate cursor-pointer hover:opacity-80 hover:shadow-sm focus:outline-none focus:ring-1 focus:ring-ring transition-all ${TYPE_STYLES[ev.type].chip}`}
                          title={ev.label}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TYPE_STYLES[ev.type].dot}`} />
                          <span className="truncate">{ev.label}</span>
                        </div>
                      ))}
                      {dayEvents.length > 4 && (
                        <div className="text-[9px] text-muted-foreground px-1.5">+{dayEvents.length - 4} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Upcoming Sidebar */}
        <div className="w-56 border-l bg-background overflow-auto p-3 flex-shrink-0">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">Upcoming</span>
          </div>
          <div className="space-y-2">
            {upcoming.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                No upcoming events.
              </div>
            ) : (
              upcoming.map((ev, idx) => {
                const [, month, day] = ev.date.split("-");
                const d = new Date(parseInt(ev.date.split("-")[0]), parseInt(month) - 1, parseInt(day));
                const label = d.toLocaleDateString("default", { month: "short", day: "numeric" });
                const style = TYPE_STYLES[ev.type];
                return (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleEventClick(ev, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleEventClick(ev, e);
                      }
                    }}
                    className="flex items-start gap-2 p-2 rounded-lg border border-border hover:bg-muted/20 hover:shadow-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring transition-all"
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${style.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground leading-snug truncate">{ev.label}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[10px] text-muted-foreground">{label}</span>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${style.chip}`}>
                          {style.label}
                        </span>
                        {ev.vendor && (
                          <span className="text-[9px] text-muted-foreground truncate">{ev.vendor}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Task edit dialog */}
      <AddTaskDialog
        open={!!editingTask}
        onOpenChange={(open) => {
          if (!open) setEditingTask(null);
        }}
        task={editingTask ?? undefined}
      />

      {/* Freight detail dialog */}
      <FreightDetailDialog
        freight={viewingFreight}
        alertType={viewingFreightAlert}
        open={!!viewingFreight}
        onOpenChange={(open) => {
          if (!open) {
            setViewingFreight(null);
            setViewingFreightAlert(null);
          }
        }}
      />
    </div>
  );
}

(CalendarPage as unknown as { path: string }).path = "/calendar";
